import { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";

import { type ColumnVersionSnapshot } from "../../../hooks/useColumnHistory";
import { buildColumnVersionDiff } from "../../../lib/columnVersionDiff";
import { type ProcedureColumn } from "../../../models/audit/auditSession";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { LoadingSpinner } from "../../ui/loadingSpinner/loadingSpinner";
import { Switch } from "../../ui/switch";
import { ColumnVersionCompare } from "./ColumnVersionCompare";
import { VersionRail } from "./VersionRail";

export interface ColumnHistoryViewProps {
  currentColumn: ProcedureColumn;
  history: ColumnVersionSnapshot[];
  loading: boolean;
  onBackToEdit: () => void;
  onRestore: (snapshot: ColumnVersionSnapshot) => void;
  resolveAttributeName?: (attributeId: string) => string;
  resolveChangedBy?: (changedBy: string) => string;
  hasDocumentCategories?: boolean;
  /** Hide the Assigned Attribute diff row — for callers whose current column
   * cannot represent the attribute link (the wizard's draft support columns). */
  hasAssignedAttribute?: boolean;
  /** Custom display label per version (defaults to "v{n}"). Lets callers slot
   * pseudo-entries — e.g. the revise wizard's "Saved" baseline — into the rail. */
  versionLabel?: (version: number) => string;
}

/**
 * The body of history mode in the column version history feature.
 *
 * Owns the selected-version and hide-unchanged state. Delegates the diff
 * display to ColumnVersionCompare and the version list to VersionRail.
 * Does NOT fetch — history/loading come from the parent (FE6) via props.
 */
export function ColumnHistoryView({
  currentColumn,
  history,
  loading,
  onBackToEdit,
  onRestore,
  resolveAttributeName,
  resolveChangedBy,
  hasDocumentCategories,
  hasAssignedAttribute,
  versionLabel = (version) => `v${version}`,
}: ColumnHistoryViewProps) {
  const [selectedVersion, setSelectedVersion] = useState<number | null>(
    history.length > 0 ? history[0].version : null,
  );
  const [hideUnchanged, setHideUnchanged] = useState(false);

  // When history loads (or changes), default the selection to the newest
  // version — but only if the current selection is no longer in the list.
  useEffect(() => {
    const selectionStillPresent = history.some(
      (h) => h.version === selectedVersion,
    );
    if (!selectionStillPresent) {
      setSelectedVersion(history.length > 0 ? history[0].version : null);
    }
  }, [history, selectedVersion]);

  const selectedSnapshot = history.find((h) => h.version === selectedVersion);

  const diffFields =
    selectedSnapshot !== undefined
      ? buildColumnVersionDiff(
          selectedSnapshot.snapshot,
          currentColumn,
          resolveAttributeName,
          hasDocumentCategories,
          hasAssignedAttribute,
        )
      : [];

  const oldLabel =
    selectedVersion !== null ? versionLabel(selectedVersion) : "—";

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBackToEdit}>
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
          <span className="text-sm font-semibold text-stone-700">
            Version history
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Label
            htmlFor="hide-unchanged-toggle"
            className="text-sm text-stone-600 cursor-pointer"
          >
            Hide unchanged
          </Label>
          <Switch
            id="hide-unchanged-toggle"
            checked={hideUnchanged}
            onCheckedChange={setHideUnchanged}
          />
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : history.length === 0 || selectedSnapshot === undefined ? (
        <div className="flex items-center justify-center py-16 text-sm text-stone-400">
          No previous versions yet
        </div>
      ) : (
        <div className="flex gap-4 flex-1 min-h-0">
          {/* Left pane — diff */}
          <div className="flex-1 min-w-0 min-h-0 flex flex-col border border-stone-200 rounded-md p-4">
            <ColumnVersionCompare
              fields={diffFields}
              hideUnchanged={hideUnchanged}
              oldLabel={oldLabel}
              currentLabel="Current"
            />
          </div>

          {/* Right pane — version rail (scrolls independently of the diff) */}
          <div className="overflow-y-auto min-h-0 shrink-0">
            <VersionRail
              history={history}
              selectedVersion={selectedVersion}
              onSelectVersion={setSelectedVersion}
              onRestore={onRestore}
              resolveChangedBy={resolveChangedBy}
              versionLabel={versionLabel}
            />
          </div>
        </div>
      )}
    </div>
  );
}
